/**
 * What `propmap prod` emitted, and how to read it.
 *
 * The production client is sent a *baked* map: one byte per cell per category,
 * with every knob in `propmap/mixer.py` already applied. So there is no measure
 * stage here, no re-ranking, no site search and no float32 -- the four sliders
 * and the schools phase are the only live inputs, and everything else in this
 * file is a description of bytes that arrived ready to draw.
 *
 * The manifest is the contract. Nothing in the client hardcodes a category, a
 * grid, a colour or a legend: they all come from here, so a fifth category is a
 * fifth element of `layers` and not an edit to the interface.
 */

import { METRICS_HEADER, countAsset, countAssetError, flushHeader } from "./metrics.ts";

export interface AssetRecord {
  path: string;
  dtype?: "uint8" | "uint16" | "float32";
  shape?: number[];
  bytes: number;
}

/** A layer's own sampling lattice in the OSGB kilometre grid. */
export interface GridSpec {
  x0: number;
  y0: number;
  step: number;
  nx: number;
  ny: number;
}

/** The one choice the product keeps: which schools count. */
export interface PhaseSpec {
  default: string;
  labels: Record<string, string>;
  order: string[];
  assets: Record<string, AssetRecord>;
}

/**
 * The places transport can be measured to instead of its default. Each is a
 * baked raster like a phase, but there are hundreds, so only the index comes
 * with the manifest and a raster is fetched when a reader picks it.
 */
export interface DestinationsSpec {
  /** what the layer measures to until a reader picks somewhere */
  default_label: string;
  count: number;
  /** a `Destination` per place, busiest first */
  index: AssetRecord;
}

/** One place on the menu. */
export interface Destination {
  key: string;
  label: string;
  lon: number;
  lat: number;
  /** its share of London's morning-peak rail arrivals */
  share: number;
  asset: AssetRecord;
}

/**
 * Where a layer applies at all, baked. Price is only a claim about somewhere a
 * home can be bought, so while it carries weight everything drawn is
 * multiplied by this and open ground fades off the map.
 */
export interface PresenceSpec {
  label: string;
  note: string;
  asset: AssetRecord;
}

export type ActivationKind = "power" | "exponential" | "logistic";
export type BlendKind = "product" | "geometric" | "arithmetic" | "power";

export interface LayerSpec {
  name: string;
  title: string;
  means: string;
  units: string;
  colour: string;
  start_weight: number;
  /** what the ramp means at each end, worst first */
  legend: [string, string];
  grid: GridSpec;
  activation: ActivationKind;
  k: number;
  source: string;
  note: string;
  phases: PhaseSpec | null;
  presence: PresenceSpec | null;
  destinations: DestinationsSpec | null;
  score: AssetRecord;
}

/** The frozen mixer: stages 3 and 4, which still read the sliders. */
export interface BlendSpec {
  colour: string;
  legend: [string, string];
  weight_max: number;
  blend: BlendKind;
  power_p: number;
  blend_floor: number;
  cutoff: number;
  display_gamma: number;
  ramp_floor: number;
  sampling: number;
  edge_soft: number;
  edge_fade: number;
  formula: string;
}

export interface CrimePointsSpec {
  count: number;
  note: string;
  period: string;
  xy: AssetRecord;
  n: AssetRecord;
  street: AssetRecord;
  /** the street names, fetched only when a dot is first tapped */
  streets: AssetRecord;
  /** the category names, in the column order of `by_category` */
  categories: string[];
  /** offences per point per category, one row a point; fetched on the first tap */
  by_category: AssetRecord;
}

export interface SchoolPointsSpec {
  count: number;
  note: string;
  phases: string[];
  grades: string[];
  list: AssetRecord;
}

export interface TransportPointsSpec {
  modes: string[];
  note: string;
  stations: AssetRecord;
  station_count: number;
  bus_count: number;
  bus_xy: AssetRecord;
}

export interface PointsSpec {
  crime?: CrimePointsSpec;
  schools?: SchoolPointsSpec;
  transport?: TransportPointsSpec;
}

export interface RailwaysSpec {
  classes: string[];
  count: number;
  asset: AssetRecord;
  source: string;
}

/** The published figures a tap opens, and the raster that resolves the tap. */
export interface AreasSpec {
  count: number;
  lsoa_count_england: number;
  london_median_price: number | null;
  price_period: string;
  met_months: [string, string];
  cell: AssetRecord;
  grid: GridSpec;
  figures: AssetRecord;
  outlines: AssetRecord;
}

export interface LatticeSpec {
  n_lon: number;
  n_lat: number;
  lon0: number;
  lon1: number;
  lat0: number;
  lat1: number;
  bounds: [number, number, number, number];
  osgb_km: AssetRecord;
}

export interface QuantisationSpec {
  levels: number;
  no_data: number;
  formula: string;
}

export interface PointQuantisationSpec {
  scale: number;
  formula: string;
}

export interface Manifest {
  generator: string;
  bounds_wgs84: [number, number, number, number];
  bounds_osgb_km: [number, number, number, number];
  lattice: LatticeSpec;
  quantisation: QuantisationSpec;
  points_quantisation: PointQuantisationSpec;
  layers: LayerSpec[];
  points: PointsSpec;
  railways: RailwaysSpec | null;
  areas: AreasSpec;
  blend: BlendSpec;
  total_bytes: number;
}

/** The figures per LSOA, as parallel arrays indexed by the cell raster minus one. */
export interface AreaFigures {
  code: string[];
  name: string[];
  msoa_name: (string | null)[];
  crime_decile: (number | null)[];
  imd_decile: (number | null)[];
  offences: (number | null)[];
  connectivity: (number | null)[];
  median_price: (number | null)[];
}

/**
 * One entry per area, in the order of `AreaFigures.code`: its polygons, each a
 * list of rings as Google encoded polylines, outer ring first.
 */
export type AreaOutlines = (string[][] | null)[];

/** The schools, as parallel arrays. */
export interface SchoolList {
  lon: number[];
  lat: number[];
  x: number[];
  y: number[];
  name: string[];
  name_i: number[];
  phase_i: number[];
  grade_i: number[];
  quality: (number | null)[];
  inspected: (number | null)[];
  pupils: (number | null)[];
}

/** The stations, as parallel arrays. */
export interface StationList {
  lon: number[];
  lat: number[];
  x: number[];
  y: number[];
  name: string[];
  lines: string[];
  mode: string[];
}

export const DATA_BASE =
  (import.meta.env["VITE_PROD_DATA"] as string | undefined) ?? "/prod-data/";

const ARRAYS = {
  uint8: Uint8Array,
  uint16: Uint16Array,
  float32: Float32Array,
} as const;

export type TypedArray = Uint8Array | Uint16Array | Float32Array;

/**
 * The binaries are little-endian, which every platform this runs on is. A
 * big-endian reader would draw a plausible map of nonsense, so it says so
 * instead.
 */
export function assertLittleEndian(): void {
  const probe = new Uint16Array([1]);
  if (new Uint8Array(probe.buffer)[0] !== 1) {
    throw new Error("propmap: this build reads little-endian assets");
  }
}

/**
 * Every request the product makes, with the client's own counters riding along.
 *
 * There is no application server here -- these are files -- so the only chance
 * to say anything about how the app is behaving is a request it was making
 * anyway, and this is all of them. The header is offered per request and the
 * offer is usually declined: `flushHeader` only produces one when a report is
 * actually due, which is at most once a minute.
 *
 * Same-origin only, because a custom header on a cross-origin request turns a
 * plain GET into a preflighted one, and a data host that answers the GET may
 * well not answer the OPTIONS. A report is never worth failing a fetch for.
 */
async function get(url: string): Promise<Response> {
  const init: RequestInit = {};
  if (sameOrigin(url)) {
    const flush = flushHeader();
    if (flush !== null) init.headers = { [METRICS_HEADER]: flush };
  }

  const started = performance.now();
  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (cause) {
    countAssetError();
    throw cause;
  }
  if (res.ok) countAsset(performance.now() - started);
  else countAssetError();
  return res;
}

function sameOrigin(url: string): boolean {
  try {
    return new URL(url, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}

export async function loadManifest(base = DATA_BASE): Promise<Manifest> {
  const res = await get(`${base}manifest.json`);
  if (!res.ok) {
    throw new Error(
      `propmap: no manifest at ${base} (HTTP ${res.status}) -- run:  ` +
        `.venv/bin/python -m propmap prod`,
    );
  }
  return (await res.json()) as Manifest;
}

export async function loadArray(record: AssetRecord, base = DATA_BASE): Promise<TypedArray> {
  const res = await get(base + record.path);
  if (!res.ok) throw new Error(`propmap: ${record.path} (HTTP ${res.status})`);
  const buffer = await res.arrayBuffer();
  const kind = ARRAYS[record.dtype ?? "uint8"];
  return new kind(buffer);
}

export async function loadJson<T>(record: AssetRecord, base = DATA_BASE): Promise<T> {
  const res = await get(base + record.path);
  if (!res.ok) throw new Error(`propmap: ${record.path} (HTTP ${res.status})`);
  return (await res.json()) as T;
}
